/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import firebase from 'firebase/compat/app';
import { AngularFireDatabase, SnapshotAction } from '@angular/fire/compat/database';
import { AbstractTicketIndexQuery } from './abstract-ticket-index-query';
import { Observable, merge } from 'rxjs';
import { TicketIndexLogger } from '../ticket-index-logger.service';
import { ITicketIndexData } from '../ticket-index-data';

export type OrderByField = 'keyStart' | 'keyFinished';

/**
 * Frankly, the best documentation for this class exists in the "How TicketIndexQuery Works" page:
 *
 *     https://mocasystems.atlassian.net/wiki/spaces/DI/pages/2328068126/How+TicketIndexQuery+Works
 */
export class DateCentricIndexQuery extends AbstractTicketIndexQuery {
	// These can be changed after a DateCentricIndexQuery is constructed and first given to a consuming TicketIndexQuery;
	// the modified query can then be re-sent to the TicketIndexQuery.
	// See also (below):  "extendAfterDate" and "extendBeforeDate"
	public orderByField: OrderByField;
	public date: string;
	public numberBeforeDate: number;
	public numberAfterDate: number;

	constructor(
		db: AngularFireDatabase,
		protected logger: TicketIndexLogger,
		orderByField: OrderByField,
		date: string, // in the format YYYY-MM-DD
		numberBeforeDate, // an integer
		numberAfterDate // an integer
	) {
		super(db, logger);
		this.orderByField = orderByField;
		this.date = date;
		this.numberBeforeDate = numberBeforeDate;
		this.numberAfterDate = numberAfterDate;
	}

	public getIndexSnapshot$(projectId: string, planId: string): Observable<SnapshotAction<ITicketIndexData>> {
		const beforeQueryFunc = (ref: firebase.database.Reference): firebase.database.Query => {
			return ref.orderByChild(this.orderByField).endAt(this.date).limitToLast(this.numberBeforeDate);
		};
		const beforeQuery$ = super.simpleQuerySnapshot$(projectId, planId, beforeQueryFunc);

		const afterQueryFunc = (ref: firebase.database.Reference): firebase.database.Query => {
			return ref.orderByChild(this.orderByField).startAt(this.date).limitToFirst(this.numberAfterDate);
		};
		const afterQuery$ = super.simpleQuerySnapshot$(projectId, planId, afterQueryFunc);

		return merge(beforeQuery$, afterQuery$);
	}

	public extendAfterDate(delta: number) {
		this.numberAfterDate += delta;
	}

	public extendBeforeDate(delta: number) {
		this.numberBeforeDate += delta;
	}
}
